import BaseLayer from 'ol/layer/Base'
import ImageLayer from 'ol/layer/Image'
import ImageStatic from 'ol/source/ImageStatic'
import type ImageSource from 'ol/source/Image'
import { WcsRgbLayer } from '../state'
import { loadWcsRgbOverlay } from '../map/wcs_rgb_geotiff'

const layerCache = new Map<string, ImageLayer<ImageSource>>()
const loadingKeys = new Set<string>()

function cacheKey(layer: WcsRgbLayer): string {
  const bands = (layer.rgbBands ?? [1, 2, 3]).join(',')
  const extent = layer.nativeExtent.join(',')
  return [
    layer.coverageId,
    layer.nativeCrs ?? 'EPSG:32651',
    extent,
    bands,
    layer.stretchMin ?? '',
    layer.stretchMax ?? '',
    layer.scaleFactor ?? '',
    layer.externalLink ?? '',
  ].join('|')
}

function emptyImageLayer(opacity: number, key: string): ImageLayer<ImageSource> {
  const olLayer = new ImageLayer({ opacity })
  olLayer.set('wcsRgbKey', key)
  olLayer.set('name', `wcsRgb:${key}`)
  return olLayer
}

export function reifyWcsRgbLayer(layer: WcsRgbLayer, existingLayer: BaseLayer | null): BaseLayer {
  const key = cacheKey(layer)
  const cached = layerCache.get(key)
  if (cached) {
    cached.setOpacity(layer.opacity)
    return cached
  }

  if (existingLayer instanceof ImageLayer && existingLayer.get('wcsRgbKey') === key) {
    existingLayer.setOpacity(layer.opacity)
    layerCache.set(key, existingLayer)
    return existingLayer
  }

  const olLayer = emptyImageLayer(layer.opacity, key)
  layerCache.set(key, olLayer)

  if (!loadingKeys.has(key)) {
    loadingKeys.add(key)
    void loadWcsRgbOverlay(layer)
      .then(data => {
        const current = layerCache.get(key)
        if (!current) return
        current.setSource(
          new ImageStatic({
            url: data.imageDataUrl,
            imageExtent: data.extent3857,
            projection: 'EPSG:3857',
          })
        )
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('[wcs rgb] Failed to load coverage', layer.coverageId, err)
        layerCache.delete(key)
      })
      .finally(() => {
        loadingKeys.delete(key)
      })
  }

  return olLayer
}
